import { useCallback, useRef, useState } from "react";
import { createClaudeClient } from "../utils/claudeApi";

/**
 * Gestion centralisée des appels Claude/OpenAI (non-stream + stream).
 * Fallback automatique entre les endpoints configurés.
 */
export function useClaudeChat(options = {}) {
  const clientRef = useRef(null);
  const abortRef = useRef(null);

  if (!clientRef.current) {
    clientRef.current = createClaudeClient(options.clientOptions || {});
  }

  const client = clientRef.current;

  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [activeEndpoint, setActiveEndpoint] = useState(null);
  const [usage, setUsage] = useState(null);
  const [attempts, setAttempts] = useState([]);

  const abort = useCallback(() => {
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (err) {
        console.warn("Abort controller error", err);
      }
      abortRef.current = null;
    }
    setStatus("idle");
  }, []);

  const sendMessage = useCallback(
    async (payload, callOptions = {}) => {
      if (abortRef.current) {
        abort();
      }
      setStatus("sending");
      setError(null);
      setUsage(null);
      try {
        const result = await client.sendMessage(payload, callOptions);
        setActiveEndpoint(result.endpoint || null);
        setAttempts(result.attempts || []);
        setUsage(result.data?.usage || null);
        setStatus("idle");
        return result;
      } catch (err) {
        setStatus("idle");
        setError(err);
        if (Array.isArray(err?.attempts)) {
          setAttempts(err.attempts);
        }
        throw err;
      }
    },
    [abort, client]
  );

  const streamMessage = useCallback(
    async (payload, handlers = {}) => {
      abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setStatus("streaming");
      setError(null);

      const {
        onStart,
        onToken,
        onUsage: onUsageHandler,
        onDone,
        onError: onErrorHandler,
        ...rest
      } = handlers || {};

      try {
        const result = await client.streamMessage(payload, {
          ...rest,
          signal: controller.signal,
          onStart: (info) => {
            setActiveEndpoint(info.endpoint || null);
            onStart?.(info);
          },
          onToken: (token, ctx) => {
            onToken?.(token, ctx);
          },
          onUsage: (nextUsage, ctx) => {
            if (nextUsage) setUsage(nextUsage);
            onUsageHandler?.(nextUsage, ctx);
          },
          onDone: (summary) => {
            setStatus("idle");
            onDone?.(summary);
          },
          onError: (err, ctx) => {
            setError(err);
            onErrorHandler?.(err, ctx);
          },
        });

        setAttempts(result.attempts || []);
        setUsage(result.data?.usage || null);
        setStatus("idle");
        abortRef.current = null;
        return result;
      } catch (err) {
        if (err?.name === "AbortError") {
          // Abort is expected when the caller cancels the request.
          setStatus("idle");
        } else {
          setStatus("idle");
          setError(err);
          if (Array.isArray(err?.attempts)) {
            setAttempts(err.attempts);
          }
        }
        abortRef.current = null;
        throw err;
      }
    },
    [abort, client, activeEndpoint]
  );

  const isStreaming = status === "streaming";
  const isSending = status === "sending";
  const isBusy = status !== "idle";

  return {
    sendMessage,
    streamMessage,
    abort,
    isStreaming,
    isSending,
    isBusy,
    status,
    error,
    usage,
    attempts,
    activeEndpoint,
    endpoints: client.endpoints,
  };
}
